import React, { useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Switch } from "@progress/kendo-react-inputs";
import { WORKFLOW_VARIABLE_LABELS } from "../Workflow/WorkflowVariables/workflowVariables.constants";
import ShowIfPopup from "./ShowIfPopup";

/**
 * Form-specific “Show If” toggle for dropdown variables: enables conditional visibility and opens
 * the expression builder dialog (mirrors SwitchNode → SwitchNodePopup behavior).
 *
 * @param {Object} props
 * @param {Function} props.updateFormChipProperty - Persists a single chip attribute via useFormChipHelper.
 * @returns {JSX.Element}
 * @author Abhishek Dhillon
 * @parent DropdownConfiguration
 */
const ShowIfSection = ({ handleShowIfChange, selectedElement, selectedSubsection }) => {
  const [showShowIfDialog, setShowShowIfDialog] = useState(false);
  const showIfTarget = selectedElement ?? selectedSubsection ?? {};

  const openDialog = () => setShowShowIfDialog(true);
  const closeDialog = () => setShowShowIfDialog(false);

  const handleSubmit = async (expression) => {
    if (showIfTarget?.id) {
      await handleShowIfChange?.(showIfTarget.id, { showIfExpression: expression });
    }
    closeDialog();
  };

  const handleToggleChange = () => {
    const showIfValue = showIfTarget?.showIf ?? false;
    handleShowIfChange?.(showIfTarget?.id, { showIf: !showIfValue });
  };

  /**
   * Opens the Show If dialog when Enter or Space is pressed on the edit link.
   * @author Abhishek Dhillon
   * @param {React.KeyboardEvent} event
   */
  const handleEditShowIfKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openDialog();
    }
  };

  return (
    <>
      <div className='form-group mb-1'>
        <div className='d-flex align-items-center justify-content-between'>
          <label htmlFor='show-if-conf' className='mr-3 font-weight-normal'>
            {WORKFLOW_VARIABLE_LABELS.SHOW_IF}
            {showIfTarget?.showIf && (
              <span
                id='edit-show-if-condition-link'
                className='cursor-pointer link-text'
                role='button'
                tabIndex={0}
                onKeyDown={handleEditShowIfKeyDown}
                onClick={(e) => {
                  e.preventDefault();
                  openDialog();
                }}>
                ({WORKFLOW_VARIABLE_LABELS.EDIT_SHOW_IF_CONDITION})
              </span>
            )}
          </label>
          <Switch
            id='show-if-conf'
            className='mb-1 toggle-switch-margin'
            checked={showIfTarget?.showIf ?? false}
            onChange={() => {
              handleToggleChange();
            }}
          />
        </div>
      </div>

      {showShowIfDialog && (
        <Dialog show={showShowIfDialog} onClose={closeDialog}>
          <ShowIfPopup
            key={showIfTarget?.id ?? "show-if"}
            onClose={closeDialog}
            selectedElement={selectedElement}
            selectedSubsection={selectedSubsection}
            handleSubmit={handleSubmit}
          />
        </Dialog>
      )}
    </>
  );
};

export default ShowIfSection;
